import Color from 'color';
import { UI_COLOR_TOKENS } from '@/data/uiColorTokens';

interface SiteStore {
    site: {
        primaryColor: string;
        secondaryColor: string;
        sceneBackgroundColor: string;
        /** Surcharges des couleurs de la nouvelle UI (variables --nu-*). */
        uiColors?: Record<string, string> | null;
    };
}

interface SiteColors {
    primary: string;
    secondary: string;
    card: string;
    background: string;
}

interface ColorShades {
    [key: string]: string;
}

export function updateCSSVariables(siteStore: SiteStore) {
    const root = document.documentElement;
    const colors: SiteColors = {
        primary: siteStore.site.primaryColor || '#40475B',
        secondary: siteStore.site.secondaryColor || '#FFFFFF',
        card: '#40475B',
        background: siteStore.site.sceneBackgroundColor || '#40475B'
    };

    for (const [key, color] of Object.entries(colors)) {
        const shades: ColorShades = generateShades(color);
        for (const [shade, value] of Object.entries(shades)) {
            root.style.setProperty(`--color-${key}-${shade}`, value);
        }
    }

    root.style.setProperty('--color-background', colors.background);

    // Nouvelle UI : surcharge des variables --nu-* configurées au BO (bloc
    // « Interface & 3D »). N'applique QUE les clés fournies → les autres gardent
    // le défaut de ui-new.scss (forward-compat, aucune régression).
    applyUiColorOverrides(siteStore.site.uiColors);
}

const UI_COLOR_OVERRIDE_STYLE_ID = 'nu-color-overrides';

/**
 * Applique les surcharges `--nu-*` du site (clés = suffixes, cf. uiColorTokens).
 *
 * ATTENTION : les variables `--nu-*` sont définies sur `.ui-new` (styles/ui-new.scss),
 * PAS sur `:root`. Poser la surcharge sur `document.documentElement` ne suffit donc
 * pas : à l'intérieur d'un sous-arbre `.ui-new`, la déclaration locale `.ui-new { --nu-…}`
 * masque la valeur héritée du `<html>`. On injecte donc une feuille de style ciblant
 * `.ui-new` (et `:root` pour tout usage hors coque) avec `!important`, ce qui gagne la
 * cascade quel que soit l'ordre de chargement. Un seul `<style>` réutilisé/mis à jour.
 */
export function applyUiColorOverrides(uiColors: Record<string, string> | null | undefined) {
    if (typeof document === 'undefined') {
        return;
    }
    const existing = document.getElementById(UI_COLOR_OVERRIDE_STYLE_ID);

    const cssVarByKey = new Map(UI_COLOR_TOKENS.map((t) => [t.key, t.cssVar]));
    // Déclarations --nu-* (valables partout : :root ET .ui-new).
    const nuDecls: string[] = [];
    // Déclarations dérivées réservées à .ui-new (accents « primary » + dégradés de
    // marque, sinon on écraserait la couleur primaire legacy globale).
    const uiNewOnlyDecls: string[] = [];
    let magentaHex: string | null = null;
    const setKeys = new Set<string>();

    if (uiColors && typeof uiColors === 'object') {
        for (const [key, value] of Object.entries(uiColors)) {
            const cssVar = cssVarByKey.get(key);
            if (!cssVar || typeof value !== 'string' || !value.trim()) {
                continue;
            }
            const hex = value.startsWith('#') ? value : `#${value}`;
            if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
                nuDecls.push(`${cssVar}: ${hex} !important;`);
                setKeys.add(key);
                if (key === 'magenta') {
                    magentaHex = hex;
                }
            }
        }
    }

    // FONDS LÉGERS (« wash ») : teintes très claires de l'accent, utilisées comme
    // arrière-plans (états actifs, badges, surlignages). Ce sont des tokens BO à
    // part entière — mais tant que l'admin ne les a PAS choisis explicitement, ils
    // doivent suivre l'accent : changer le magenta sans ça laissait tous les fonds
    // en rose #fde7f1. La map `uiColors` du BO est CREUSE (elle ne contient que les
    // clés réellement modifiées, cf. WebsiteColors.setUiColor/resetUiColor), donc
    // « clé absente » = « jamais choisi » : une valeur explicite gagne toujours.
    // Les pourcentages reproduisent EXACTEMENT les défauts canoniques (#fde7f1 pour
    // #ec1e78) → aucun écart pour un site qui n'a pas touché à l'accent.
    for (const [accentKey, washKey, pct] of [
        ['magenta', 'magenta-wash', 10.5],
        ['cyan', 'cyan-wash', 12]
    ] as const) {
        const accent = setKeys.has(accentKey) ? uiColors?.[accentKey] : null;
        if (accent && !setKeys.has(washKey)) {
            const cssVar = cssVarByKey.get(washKey);
            const hex = accent.startsWith('#') ? accent : `#${accent}`;
            if (cssVar) {
                nuDecls.push(`${cssVar}: color-mix(in srgb, ${hex} ${pct}%, #ffffff) !important;`);
            }
        }
    }

    // L'accent « magenta » alimente aussi, dans la nouvelle coque, les nuances
    // --color-primary-* et les dégradés de marque (ui-new.scss les fige en dur sur
    // .ui-new). On les redérive du magenta choisi pour que l'accent couvre TOUT.
    if (magentaHex) {
        const shades = generateShades(magentaHex);
        for (const shade of [400, 500, 600, 700, 800] as const) {
            uiNewOnlyDecls.push(`--color-primary-${shade}: ${shades[shade]} !important;`);
        }
        const lighter = `color-mix(in srgb, ${magentaHex} 55%, #ffffff)`;
        uiNewOnlyDecls.push(`--gradient-brand: linear-gradient(135deg, ${magentaHex}, ${lighter}) !important;`);
        uiNewOnlyDecls.push(`--gradient-brand-soft: color-mix(in srgb, ${magentaHex} 8%, transparent) !important;`);
    }

    // Aucune surcharge valide → retirer la feuille éventuelle (retour aux défauts).
    if (nuDecls.length === 0 && uiNewOnlyDecls.length === 0) {
        existing?.remove();
        return;
    }

    const nuBlock = nuDecls.join(' ');
    const css = `:root{${nuBlock}} .ui-new{${nuBlock} ${uiNewOnlyDecls.join(' ')}}`;
    const styleEl = (existing as HTMLStyleElement | null) ?? document.createElement('style');
    if (!existing) {
        styleEl.id = UI_COLOR_OVERRIDE_STYLE_ID;
        document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;
}

export function generateShades(baseColor: string): ColorShades {
    const color = Color(baseColor);
    const hsl = color.hsl();

    const baseLightness = hsl.color[2];
    const lighterRange = 100 - baseLightness;
    const darkerRange = baseLightness;

    const getLightShade = (step: number, totalSteps: number) =>
        Color.hsl(hsl.color[0], hsl.color[1], baseLightness + (step / totalSteps) * lighterRange).hex();

    const getDarkShade = (step: number, totalSteps: number) =>
        Color.hsl(hsl.color[0], hsl.color[1], baseLightness - (step / totalSteps) * darkerRange).hex();

    return {
        50: getLightShade(4.5, 5),
        100: getLightShade(4, 5),
        200: getLightShade(3, 5),
        300: getLightShade(2, 5),
        400: getLightShade(1, 5),
        500: baseColor,
        600: getDarkShade(1, 5),
        700: getDarkShade(2, 5),
        800: getDarkShade(3, 5),
        900: getDarkShade(4, 5),
        950: getDarkShade(4.5, 5)
    };
}
